<script setup>
import { ref, shallowRef, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChevronLeft } from '@lucide/vue';
import { useCustomerStore } from '../store/customer';
import { useScheduleStore } from '../store/schedule';
import router from '../routes';
import { extractAxiosErrorMessage } from '@client/js/shared/utils.js';

// Components
import DateTimePicker from './DateTimePicker.vue';
import LineItems from './LineItems.vue';
import OrderTotal from './OrderTotal.vue';
import ModalHead from './ModalHead.vue';
import ModalBtn from './ModalBtn.vue';
import ModalFooter from './ModalFooter.vue';
import FieldGroup from '@client/js/components/FieldGroup.vue';
import Accordion from '@client/js/components/Accordion.vue';
import CustomerFields from '@client/js/components/CustomerFields.vue';
import DeliveryFields from '@client/js/components/DeliveryFields.vue';
import CustomerCard from '@client/js/components/CustomerCard.vue';
import ExpandableTextarea from '@client/js/components/ExpandableTextarea.vue';

const rootData = inject('rootData');
const restApi = inject('restApi');
const { t } = useI18n();

const scheduleStore = useScheduleStore();
const customerStore = useCustomerStore();

const inProgress = ref(false);

const customerResponseMessage = ref('');

const handleCustomer = async () => {
    customerResponseMessage.value = '';
    if (customerStore.customerId) {
        await router.push('/payment-options');
        return true;
    }

    inProgress.value = true;

    const requestBody = {
        phoneNumber: customerStore.phoneNumber,
        email: customerStore.email,
        firstName: customerStore.firstName,
        lastName: customerStore.lastName,
        orderType: customerStore.orderType,
        street: customerStore.street,
        address2: '', // just stub for now
        city: customerStore.city,
        state: customerStore.state,
        zip: customerStore.zip,
        lat: customerStore.lat,
        lng: customerStore.lng,
        message: '', // honeypot
    };

    try {
        const response = await restApi.post('clover/create-customer', requestBody);
        const result = response?.data ?? {}; // its still axios overall data, not only response data

        const customerId = result?.data?.customer_id ?? null;

        if (!customerId) {
            throw new Error('Customer ID was not returned');
        }

        customerStore.customerId = customerId;

        await router.push('/payment-options');
        return true;
    } catch (error) {
        const result = error?.response?.data ?? null;
        const message = result?.message ?? extractAxiosErrorMessage(error);

        console.error('Create customer failed:', {
            message,
            code: result?.code ?? 'customer_request_failed',
            data: result?.data ?? null,
            meta: result?.meta ?? null,
            state: 'error',
            error,
        });

        customerResponseMessage.value = message;
        return false;
    } finally {
        inProgress.value = false;
    }
};

// BUG: Potential error!
// const selectedDate = ref('');
// const selectedTime = ref('');

// const getSheduledDateEvent = function (dtPair) {
//     if (Array.isArray(dtPair) && dtPair.length == 2) {
//         let [day, time] = dtPair;
//         selectedDate.value = day;
//         selectedTime.value = time;
//     }
//     console.log("Emitted", selectedDate.value, selectedTime.value);
// }

</script>

<template>
    <ModalHead>
        <template #additional>
            <router-link to="/cart/">
                <ChevronLeft/>
            </router-link>
        </template>
    </ModalHead>
    <div class="modal__body">

        <date-time-picker />

        <div class="fieldsets">
            <FieldGroup class="bordered" v-if="customerStore.isCompleteCustomerData">
                <template #legend>{{ t('common.customer') }}</template>
                <CustomerCard />
            </FieldGroup>

            <FieldGroup v-else>
                <template #legend>{{ t('common.customer') }}</template>
                <CustomerFields />
            </FieldGroup>

            <FieldGroup v-if="customerStore.isDeliveryOrder">
                <template #legend>{{ t('common.deliverTo') }}</template>
                <DeliveryFields />
            </FieldGroup>

            <FieldGroup class="no-border">
                <ExpandableTextarea :label="t('common.noteLabel')" :placeholder="t('common.notePlaceholder')"
                    v-model="customerStore.instructions" :inputProps="customerStore.instructionsProps"
                    :button-open-label="t('common.addInstructions')" :button-close-label="t('common.hideInstructions')" />
            </FieldGroup>
        </div>

        <Accordion>
            <template #heading>{{ t('common.orderDetails') }}</template>
            <line-items :controls="false" :hideCash="true" />
        </Accordion>
        <OrderTotal :hideCash="true" />

        <div class="checkoutErrors" v-show="!customerStore.meta.valid || customerStore.deliveryBlocked || !scheduleStore?.isValid">
            <div class="checkoutErrorsTitle">{{ t('checkout.checkNext') }}</div>
            <div class="checkoutError" v-if="scheduleStore.errors?.selectedDate">{{
                scheduleStore.errors.selectedDate }}
            </div>
            <div class="checkoutError" v-if="scheduleStore.errors?.selectedTime">{{
                scheduleStore.errors.selectedTime }}
            </div>
            <div class="checkoutError" v-for="err in customerStore.errors">{{ err }}</div>
            <div class="checkoutError" v-if="customerStore.deliveryBlocked">{{ t('common.deliveryUnavailableAddress') }}</div>
        </div>
        <div class="requestErrors" v-show="customerResponseMessage.length > 0">
            <div class="requestError" v-text="customerResponseMessage"></div>
        </div>

    </div>
    <ModalFooter>
        <ModalBtn @click="handleCustomer" form="contact-info" class="btn-primary btn-action"
            :data-processing="inProgress || null"
            :disabled="!customerStore.meta.valid || customerStore.deliveryBlocked || !scheduleStore.selectedDate || !scheduleStore.selectedTime">
            {{ t('common.placeOrder') }}
        </ModalBtn>
    </ModalFooter>
</template>

<style lang="scss" scoped>
.requestErrors {
    background-color: #fff0f5;
    border: 2px solid #ffafbb;
    padding: 0.75rem 1.5rem 0.75rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    margin-bottom: 1.25rem;
    color: var(--text-secondary);

    &Title {
        font-weight: 700;
        letter-spacing: 0.25px;
        margin-bottom: 0.25rem;
    }
}

.checkoutError {
    margin-bottom: 0.125rem;
}

.cart-total {
    margin-left: -1rem;
    margin-right: -1rem;

}

.customer-info {

    &__note {
        margin-top: 1rem;
        font-weight: 400;
        color: #212121;

        button {
            font-weight: 300;
            cursor: pointer;
            font-size: .75rem;
            background-color: var(--c-primary);
            color: var(--c-primary-fg);
            padding: 0.5rem 0.875rem;
            letter-spacing: initial;
            border-radius: 2px;
            display: flex;
            justify-content: center;
            align-items: center;
            align-content: center;
            line-height: 0.77;
            margin-top: 0.125rem;
        }
    }

    dl {
        font-weight: 400;
        margin-bottom: 0;
        margin-top: 0;
    }

    dt {
        font-weight: 500;
        margin-right: 0.375rem;
    }

    dd:not(:last-child):after {
        content: "\a";
        white-space: pre;
    }

    dt,
    dd {
        display: inline;
        margin: 0 0 .375em;

        &:last-of-type {
            margin-bottom: 0;
        }
    }

}

dl {
    border-top: none;
}

.instructions {
    max-height: 500px;
    position: relative;
    margin-bottom: 1.875rem;

    textarea {
        line-height: 1.5;
        padding: 26px 8px 8px;
        border: 1px solid #727272;
        border-radius: 4px;
        outline: none;
    }

    label {
        color: #727272;
        font-size: .8rem;
        left: 1px;
        line-height: 1;
        margin: 8px;
        padding: 0;
        position: absolute;
        top: 1px;
        transition: font-size .2s ease;
        font-weight: 500;
    }
}

.orderItems {
    border-top: 1px solid rgba(0, 0, 0, .12);
    border-bottom: 1px solid rgba(0, 0, 0, .12);
    margin-left: -1rem;
    margin-right: -1rem;
    padding: 0 1rem;

    &Head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 500;
        font-size: 16px;
        margin: 12px 0;
        line-height: 24px;
    }

    svg {
        transition: transform 0.25s 0s linear;
        font-size: 0.75em;
    }

    &Body {
        max-height: 0;
        height: auto;
        overflow: hidden;
        transition: max-height 0.25s 0s linear;
    }

    &Active {
        .orderItemsBody {
            max-height: 10000px;
        }

        svg {
            transform: rotate(90deg);
        }

    }
}

.modal {
    &__body {
        padding: 16px;
        min-height: 80vh;
        min-height: calc(100vh - 170px);
        min-height: calc(100svh - 170px); // 146

        @media (min-width: 576px) {
            min-height: auto;
        }
    }
}

legend {
    background-color: #fff;
    color: #333333;
    z-index: 1;
    position: relative;
    cursor: default;
    user-select: none;
    font-weight: 500;
    //   padding: 0 0.25rem;
    padding: 0 0.5rem 0 0;
}

fieldset {
    background: #fff;
    color: #333;
    // border-radius: 4px;
    // border: 1px solid #727272;
    // padding: 0.75em 0.75em 0.625em;

    padding: 0.75em 0 0;
    border: none;
    border-top: 1px solid #727272;

    .wrapper {
        display: flex;
        justify-content: space-between;

        .text-field {
            flex: 1 0 calc(50% - 1rem);
        }
    }
}

.fieldsets {
    &>fieldset:not(:last-child) {
        margin-top: 1rem;
        margin-bottom: .5rem;
    }
}

.text-field {
    position: relative;
    width: 100%;
    flex: 1 0 calc(100% - 1rem);
    margin-bottom: 1.875rem;

    label {
        color: var(--text-secondary);
        line-height: 1;
        font-size: 16px;
        left: 1px;
        margin: 17px 8px 8px;
        padding: 0;
        position: absolute;
        top: 1px;
        transition: font-size .2s ease, margin-top .15s ease-in-out;
        user-select: none;
        pointer-events: none;
    }

    input {
        width: 100%;
        position: relative;
        box-shadow: none;
        overflow-y: visible;
        font-size: 1rem;
        border-radius: 0.25rem;
        line-height: 1.1875;
        margin-bottom: 0;
        margin-top: 0;
        min-width: 236px;
        width: inherit;
        max-width: 100%;
        background: #fff;
        border: 1px solid #333333;
        outline: none;
        padding: 26px 0.5rem 6px;
        color: #333333;

        &:hover {
            border-color: var(--c-primary);

            &+label {
                color: var(--c-primary);
            }
        }

        &:not(:focus)::placeholder,
        &:not(:active)::placeholder {
            color: transparent;
        }

        &:focus,
        &:active {

            &:not([disabled]) {
                border: 1px solid transparent;
                outline: 3px solid rgba(var(--c-primary-rgb), 0.5);
                border-color: var(--text-primary);
                color: var(--text-primary);


                &:not([readonly])~label {
                    margin-top: 0.5rem;
                    font-size: 0.75rem;
                }
            }

        }

        &:not(:placeholder-shown):not([value=""])~label {
            margin-top: 0.5rem;
            font-size: 0.75rem;
        }

        &::-webkit-input-placeholder,
        &::-moz-placeholder {
            color: (0, 0, 0, 0.5);
            font-size: 0.875rem;
        }
    }
}
</style>